import {
    DEFAULT_INPUT_BINDINGS,
    GAMEPLAY_INPUT_BINDING_IDS,
    MODIFIER_KEYS,
    type AnalogInputBinding,
    type DigitalInputBindingId,
    type GameplayInputBindingId,
    type InputBindings,
    type ModifierKey,
    type UiInputBindingId,
    type Vector2,
} from '@sorrem/shared';
import { BindingResolver, type BindingChange } from './binding-resolver';

/** Where the game's intents go: the page side of the game connection. */
export interface GameplayIntentSink {
    intentStart(intent: GameplayInputBindingId, position: Vector2, isPress: boolean): void;
    intentEnd(intent: GameplayInputBindingId): void;
    /** Cursor over the game canvas, in canvas drawing-buffer pixels. */
    pointerMove(position: Vector2): void;
    /** Continuous movement direction; zero when released. */
    moveDirection(direction: Vector2): void;
    /** The mouse wheel turned over the game canvas, by `deltaY` pixels as the browser reports it. */
    zoom(deltaY: number): void;
}

/** The game canvas, while a run is on screen. */
export interface GameInputSurface {
    readonly element: HTMLElement;
    /** Converts a pointer event's client coordinates to canvas drawing-buffer pixels. */
    toSurfacePosition(client: Vector2): Vector2;
    readonly sink: GameplayIntentSink;
}

/** Stick travel below this counts as centered. */
const STICK_DEADZONE = 0.25;
const ZERO: Vector2 = { x: 0, y: 0 };
const GAMEPLAY_IDS: ReadonlySet<string> = new Set(GAMEPLAY_INPUT_BINDING_IDS);
const MODIFIER_NAMES: ReadonlySet<string> = new Set(MODIFIER_KEYS);

function isGameplayId(id: DigitalInputBindingId): id is GameplayInputBindingId {
    return GAMEPLAY_IDS.has(id);
}

function toModifierSet(event: { shiftKey: boolean; ctrlKey: boolean; altKey: boolean; metaKey: boolean }): Set<ModifierKey> {
    const modifiers = new Set<ModifierKey>();
    if (event.shiftKey) modifiers.add('Shift');
    if (event.ctrlKey) modifiers.add('Control');
    if (event.altKey) modifiers.add('Alt');
    if (event.metaKey) modifiers.add('Meta');
    return modifiers;
}

function toStickDirection(pad: Gamepad, stick: 'left' | 'right'): Vector2 {
    const offset = stick === 'left' ? 0 : 2;
    const x = pad.axes[offset] ?? 0;
    const y = pad.axes[offset + 1] ?? 0;
    return Math.hypot(x, y) < STICK_DEADZONE ? ZERO : { x, y };
}

function addListener(
    listeners: Map<UiInputBindingId, Set<() => void>>,
    id: UiInputBindingId,
    listener: () => void,
): () => void {
    let set = listeners.get(id);
    if (!set) {
        set = new Set();
        listeners.set(id, set);
    }
    set.add(listener);
    return () => set.delete(listener);
}

/**
 * The one place the page captures player input. Physical keys, mouse
 * buttons and gamepad buttons are resolved through the input bindings; the
 * game canvas receives gameplay intents, and UI code subscribes to the UI
 * bindings it cares about (action bar, open menu) by name, never by key.
 *
 * Browser focus decides who owns the keyboard: a press counts only while the
 * game canvas, or nothing, has focus. Anything else focused (a text field, a
 * button, a dialog) owns its own keys, so opening a modal suppresses
 * gameplay input with no opt-in. Releases always count, and focus moving to
 * UI lets go of everything held, so nothing is left stuck down.
 */
export class PageInput {
    private bindings: InputBindings;
    private readonly resolver: BindingResolver;
    private surface: GameInputSurface | null = null;
    private pointer: Vector2 = ZERO;
    private readonly pressListeners = new Map<UiInputBindingId, Set<() => void>>();
    private readonly releaseListeners = new Map<UiInputBindingId, Set<() => void>>();
    private readonly bindingListeners = new Set<() => void>();
    private readonly gamepadSticks = new Map<'left' | 'right', Vector2>();
    private readonly gamepadButtons = new Map<number, boolean[]>();
    private sentDirection: Vector2 = ZERO;
    private gamepadFrameId: number | null = null;

    constructor(private readonly win: Window, bindings: InputBindings = DEFAULT_INPUT_BINDINGS) {
        this.bindings = bindings;
        this.resolver = new BindingResolver(bindings);

        win.addEventListener('keydown', (event) => this.handleKeyDown(event));
        win.addEventListener('keyup', (event) => this.handleKeyUp(event));
        win.addEventListener('pointerdown', (event) => this.handlePointerDown(event));
        win.addEventListener('pointermove', (event) => this.handlePointerMove(event));
        win.addEventListener('pointerup', (event) => this.handlePointerUp(event));
        win.addEventListener('pointercancel', (event) => this.handlePointerUp(event));
        // Not passive: the canvas keeps the wheel, so it never scrolls or zooms the page.
        win.addEventListener('wheel', (event) => this.handleWheel(event), { passive: false });
        win.addEventListener('blur', () => this.releaseAll());
        win.addEventListener('gamepadconnected', () => this.startGamepadPolling());
        win.document.addEventListener('focusin', (event) => {
            if (!this.ownsFocus(event.target)) {
                this.releaseAll();
            }
        });
    }

    getBindings(): InputBindings {
        return this.bindings;
    }

    /** Replaces the bindings for this session. Anything held that no longer applies is released. */
    setBindings(bindings: InputBindings): void {
        this.bindings = bindings;
        this.apply(this.resolver.setBindings(bindings));
        this.updateMoveDirection();
        for (const listener of this.bindingListeners) {
            listener();
        }
    }

    subscribeBindings(listener: () => void): () => void {
        this.bindingListeners.add(listener);
        return () => this.bindingListeners.delete(listener);
    }

    /** Calls `listener` each time a UI binding is pressed. */
    onPress(id: UiInputBindingId, listener: () => void): () => void {
        return addListener(this.pressListeners, id, listener);
    }

    /**
     * Calls `listener` each time a UI binding is let go, including when focus
     * moving to other UI releases everything. For press-and-hold bindings.
     */
    onRelease(id: UiInputBindingId, listener: () => void): () => void {
        return addListener(this.releaseListeners, id, listener);
    }

    /** Routes gameplay intents to a game canvas until the returned function is called. */
    attachSurface(surface: GameInputSurface): () => void {
        this.releaseAll();
        this.surface = surface;
        const preventContextMenu = (event: Event): void => event.preventDefault();
        surface.element.addEventListener('contextmenu', preventContextMenu);
        return (): void => {
            surface.element.removeEventListener('contextmenu', preventContextMenu);
            if (this.surface === surface) {
                this.releaseAll();
                this.surface = null;
                this.sentDirection = ZERO;
            }
        };
    }

    /** Whether a keyboard or gamepad press may count: the game canvas, or nothing, has focus. */
    private ownsFocus(target: EventTarget | null): boolean {
        const doc = this.win.document;
        return target === null
            || target === this.win
            || target === doc
            || target === doc.body
            || target === doc.documentElement
            || (this.surface !== null && target === this.surface.element);
    }

    private handleKeyDown(event: KeyboardEvent): void {
        // Programmatic events are not physical input, and feeding them back in
        // can re-arm whatever dispatched them every frame (#106).
        if (event.isTrusted === false) {
            return;
        }
        this.apply(this.resolver.setModifiers(toModifierSet(event)));
        if (MODIFIER_NAMES.has(event.key)) {
            this.pressModifierKey(event);
            return;
        }
        // Browser/OS shortcuts are not gameplay input: Ctrl+A must not hold
        // force-move down. Shift is deliberately allowed: it is the aim
        // modifier.
        if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) {
            return;
        }
        if (!this.ownsFocus(event.target)) {
            return;
        }
        this.apply(this.resolver.press({ device: 'keyboard', key: event.key }));
    }

    private handleKeyUp(event: KeyboardEvent): void {
        if (event.isTrusted === false) {
            return;
        }
        this.apply(this.resolver.setModifiers(toModifierSet(event)));
        const input = { device: 'keyboard', key: event.key } as const;
        // A lone Alt let go would focus Chrome's menu; one bound in the game is the game's.
        if (MODIFIER_NAMES.has(event.key) && this.ownsFocus(event.target) && this.resolver.isBound(input)) {
            event.preventDefault();
        }
        this.apply(this.resolver.release(input));
    }

    /**
     * A modifier pressed on its own is a key like any other when a binding is
     * on it (Alt holds item names, #274), and kept from the browser then.
     * Otherwise it only modifies: Shift turns a click into an aimed attack.
     */
    private pressModifierKey(event: KeyboardEvent): void {
        const input = { device: 'keyboard', key: event.key } as const;
        if (event.repeat || !this.ownsFocus(event.target) || !this.resolver.isBound(input)) {
            return;
        }
        event.preventDefault();
        this.apply(this.resolver.press(input));
    }

    private handlePointerDown(event: PointerEvent): void {
        // Only presses on the game canvas itself are gameplay: the HUD sits on
        // top of it and receives its own clicks.
        if (this.surface === null || event.target !== this.surface.element || event.button < 0 || event.button > 2) {
            return;
        }
        this.movePointer(event);
        this.apply(this.resolver.setModifiers(toModifierSet(event)));
        this.apply(this.resolver.press({ device: 'mouse', button: event.button }));
    }

    private handlePointerMove(event: PointerEvent): void {
        if (this.surface !== null && event.target === this.surface.element) {
            this.movePointer(event);
        }
    }

    private handlePointerUp(event: PointerEvent): void {
        this.apply(this.resolver.release({ device: 'mouse', button: event.button }));
    }

    private handleWheel(event: WheelEvent): void {
        // Like presses, only the wheel over the canvas itself: the HUD scrolls its own panels.
        if (this.surface === null || event.target !== this.surface.element) {
            return;
        }
        event.preventDefault();
        this.surface.sink.zoom(event.deltaY);
    }

    private movePointer(event: PointerEvent): void {
        if (this.surface === null) {
            return;
        }
        this.pointer = this.surface.toSurfacePosition({ x: event.clientX, y: event.clientY });
        this.surface.sink.pointerMove(this.pointer);
    }

    private releaseAll(): void {
        this.apply(this.resolver.releaseAll());
        this.gamepadButtons.clear();
    }

    private apply(changes: readonly BindingChange[]): void {
        for (const change of changes) {
            if (isGameplayId(change.id)) {
                if (this.surface === null) {
                    continue;
                }
                if (change.kind === 'start') {
                    this.surface.sink.intentStart(change.id, this.pointer, change.isPress);
                } else {
                    this.surface.sink.intentEnd(change.id);
                }
                continue;
            }
            if (change.kind === 'start' && change.isPress) {
                for (const listener of this.pressListeners.get(change.id) ?? []) {
                    listener();
                }
            } else if (change.kind === 'end') {
                for (const listener of this.releaseListeners.get(change.id) ?? []) {
                    listener();
                }
            }
        }
    }

    private startGamepadPolling(): void {
        if (this.gamepadFrameId !== null) {
            return;
        }
        const poll = (): void => {
            const pads = this.connectedGamepads();
            if (pads.length === 0) {
                this.gamepadFrameId = null;
                this.releaseAll();
                this.gamepadSticks.clear();
                this.updateMoveDirection();
                return;
            }
            this.pollGamepads(pads);
            this.gamepadFrameId = this.win.requestAnimationFrame(poll);
        };
        this.gamepadFrameId = this.win.requestAnimationFrame(poll);
    }

    private connectedGamepads(): Gamepad[] {
        const pads = this.win.navigator.getGamepads?.() ?? [];
        return Array.from(pads).filter((pad): pad is Gamepad => pad !== null && pad.connected);
    }

    /**
     * The Gamepad API has no events, only state, so press and release edges
     * are synthesized by comparing each button with the previous poll.
     */
    private pollGamepads(pads: readonly Gamepad[]): void {
        const focused = this.ownsFocus(this.win.document.activeElement);
        for (const pad of pads) {
            const previous = this.gamepadButtons.get(pad.index) ?? [];
            const current = pad.buttons.map((button) => button.pressed);
            current.forEach((pressed, button) => {
                if (pressed && !previous[button] && focused) {
                    this.apply(this.resolver.press({ device: 'gamepad', button }));
                } else if (!pressed && previous[button]) {
                    this.apply(this.resolver.release({ device: 'gamepad', button }));
                }
            });
            // A press ignored for lack of focus is left unrecorded, so a button still held when focus returns counts then.
            this.gamepadButtons.set(pad.index, current.map((pressed, button) => pressed && (focused || previous[button] === true)));
        }

        const primary = pads[0]!;
        this.gamepadSticks.set('left', focused ? toStickDirection(primary, 'left') : ZERO);
        this.gamepadSticks.set('right', focused ? toStickDirection(primary, 'right') : ZERO);
        this.updateMoveDirection();
    }

    /** The first bound direction source that is off-center wins. */
    private resolveMoveDirection(): Vector2 {
        for (const binding of this.bindings.move as readonly AnalogInputBinding[]) {
            const direction = this.gamepadSticks.get(binding.input.stick) ?? ZERO;
            if (direction.x !== 0 || direction.y !== 0) {
                return direction;
            }
        }
        return ZERO;
    }

    private updateMoveDirection(): void {
        if (this.surface === null) {
            return;
        }
        const direction = this.resolveMoveDirection();
        if (direction.x === this.sentDirection.x && direction.y === this.sentDirection.y) {
            return;
        }
        this.sentDirection = direction;
        this.surface.sink.moveDirection(direction);
    }
}

let pageInput: PageInput | null = null;

/** The page's input capture layer, created on first use. */
export function getPageInput(): PageInput {
    pageInput ??= new PageInput(window);
    return pageInput;
}
